import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { UserPlus, Gift, Wallet, Users, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 }).format(n || 0);

/**
 * Empfehlungs-Übersicht fürs Admin-Dashboard:
 * registriert / belohnt / geworbene Kunden / Umsatz durch Werbung.
 */
const ReferralStatsCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/settings/referral/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Card className="border-indigo-200 bg-indigo-50/60" data-testid="dashboard-referral-card">
        <CardContent className="flex items-center gap-2 py-6 text-indigo-700" data-testid="dashboard-referral-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Empfehlungen werden geladen...
        </CardContent>
      </Card>
    );
  }

  const stats = [
    { key: 'registered', label: 'Registriert (offen)', value: data?.registered ?? 0, icon: UserPlus, hint: 'Geworben, wartet auf erstes Training' },
    { key: 'rewarded', label: 'Belohnt', value: data?.rewarded ?? 0, icon: Gift, hint: 'Werber wurde belohnt' },
    { key: 'referred', label: 'Geworbene Kunden', value: data?.referred_customers ?? 0, icon: Users, hint: 'Kunden gesamt über Empfehlung' },
    { key: 'revenue', label: 'Umsatz durch Werbung', value: fmtCHF(data?.referral_revenue), icon: Wallet, hint: 'Bezahlte Rechnungen geworbener Kunden', highlight: true },
  ];

  return (
    <Card className="border-indigo-200 bg-indigo-50/60" data-testid="dashboard-referral-card">
      <CardContent className="space-y-4 pt-4" data-testid="dashboard-referral-content">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-indigo-900">StrongFit-Empfehlungen</p>
          <button
            onClick={() => fetchData(true)}
            className="text-indigo-600 hover:text-indigo-800 transition-colors"
            title="Aktualisieren"
            data-testid="dashboard-referral-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-referral-grid">
          {stats.map(({ key, label, value, icon: Icon, hint, highlight }) => (
            <div
              key={key}
              className={`p-3 rounded-lg bg-white border ${highlight ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-indigo-100'}`}
              data-testid={`dashboard-referral-${key}-card`}
              title={hint}
            >
              <div className="flex items-center gap-1.5 text-indigo-700">
                <Icon className="w-3.5 h-3.5" />
                <p className="text-xs">{label}</p>
              </div>
              <p className="text-xl font-bold text-indigo-900 mt-1" data-testid={`dashboard-referral-${key}-value`}>
                {value}
              </p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-indigo-800">
          <span data-testid="dashboard-referral-total">Empfehlungen gesamt: <strong>{data?.total ?? 0}</strong></span>
          <button
            onClick={() => navigate('/settings/advanced?tab=ki-api')}
            className="text-indigo-600 hover:text-indigo-800 underline"
            data-testid="dashboard-referral-manage"
          >
            Programm verwalten
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralStatsCard;
